import { matchPath } from 'react-router-dom';
import { z } from 'zod';
import {
  CHECKOUT_PAYMENT_ROUTE,
  CHECKOUT_SHIPPING_ROUTE,
  CYO_ROUTE,
  PRECHECKOUT_PLAN_ROUTE,
  ROUTE_MINI_QUIZ_BUDGET,
  ROUTE_MINI_QUIZ_BUDGET_BENEFITS,
  ROUTE_MINI_QUIZ_MEAL_FREQUENCIES,
} from '@hungryroot/routes';
import { allMiniQuizRoutes } from '../../defs/mini-quiz-routes';

export const MINI_QUIZ_TAIL_RESUME_ROUTES = [
  CYO_ROUTE,
  ROUTE_MINI_QUIZ_MEAL_FREQUENCIES,
  ROUTE_MINI_QUIZ_BUDGET,
  ROUTE_MINI_QUIZ_BUDGET_BENEFITS,
  PRECHECKOUT_PLAN_ROUTE,
  CHECKOUT_SHIPPING_ROUTE,
  CHECKOUT_PAYMENT_ROUTE,
] as const;

export type MiniQuizTailResumeRoute =
  (typeof MINI_QUIZ_TAIL_RESUME_ROUTES)[number];

const miniQuizResumeRoutesList = [
  ...allMiniQuizRoutes,
  CYO_ROUTE,
  PRECHECKOUT_PLAN_ROUTE,
  CHECKOUT_SHIPPING_ROUTE,
  CHECKOUT_PAYMENT_ROUTE,
];

export type MiniQuizResumeRoute =
  | (typeof allMiniQuizRoutes)[number]
  | typeof CYO_ROUTE
  | typeof PRECHECKOUT_PLAN_ROUTE
  | typeof CHECKOUT_SHIPPING_ROUTE
  | typeof CHECKOUT_PAYMENT_ROUTE;

export const miniQuizResumeRoutesSchema = z.enum([
  miniQuizResumeRoutesList[0],
  ...miniQuizResumeRoutesList.slice(1),
] as [MiniQuizResumeRoute, ...MiniQuizResumeRoute[]]);

export function matchMiniQuizResumeRoute(
  pathname: string,
  route: string
): boolean {
  return (
    matchPath(pathname, { path: route, exact: true, strict: false }) !== null
  );
}

export function isMiniQuizResumeRoute(pathname: string): boolean {
  return miniQuizResumeRoutesList.some((route) =>
    matchMiniQuizResumeRoute(pathname, route)
  );
}

/** Map a runtime pathname to the schema's trailing-slash route constant. */
export function canonicalizeMiniQuizResumePathname(
  pathname: string
): MiniQuizResumeRoute | undefined {
  return miniQuizResumeRoutesList.find((route) =>
    matchMiniQuizResumeRoute(pathname, route)
  ) as MiniQuizResumeRoute | undefined;
}

export function getTailResumeRoutes({
  budgetAnswered,
}: {
  budgetAnswered: boolean;
}): readonly MiniQuizTailResumeRoute[] {
  if (budgetAnswered) {
    return MINI_QUIZ_TAIL_RESUME_ROUTES;
  }

  return MINI_QUIZ_TAIL_RESUME_ROUTES.filter(
    (route) => route !== ROUTE_MINI_QUIZ_BUDGET_BENEFITS
  );
}

export function getRouteIndexInList(
  pathname: string,
  routes: readonly string[]
): number {
  return routes.findIndex((route) => matchMiniQuizResumeRoute(pathname, route));
}

/**
 * The furthest a tail re-entry may land. Checkout shipping and payment stay
 * in {@link MINI_QUIZ_TAIL_RESUME_ROUTES} so progress recorded on those pages
 * still parses, but a CTA never drops a returning guest straight into
 * checkout — anyone who had reached plan or beyond re-enters at the plan
 * page and continues to checkout from there.
 */
export function clampTailResumeRouteToPlan(route: string): string {
  const routeIndex = getRouteIndexInList(route, MINI_QUIZ_TAIL_RESUME_ROUTES);
  const planIndex = getRouteIndexInList(
    PRECHECKOUT_PLAN_ROUTE,
    MINI_QUIZ_TAIL_RESUME_ROUTES
  );

  if (routeIndex === -1 || routeIndex <= planIndex) {
    return route;
  }

  return PRECHECKOUT_PLAN_ROUTE;
}
